import React, { useEffect, useState } from 'react';
import { Routes, Route, Navigate, useLocation, useNavigate, useNavigationType, useParams, useSearchParams } from 'react-router-dom';
import Router from './components/transitions/PortalRouter';
import { isOauthHandoffPath } from './components/GoogleSignInButton';
import AppChrome from './components/chrome/AppChrome';
import { clubService, proService } from './services';
import NewHereCity from './components/newhere/NewHereCity';
import { Elements } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import { AuthProvider, useAuth } from './context/AuthContext';
import { UserProvider, useUser } from './context/UserContext';
import { ThemeProvider } from './context/ThemeContext';
import { NotificationProvider } from './context/NotificationContext';
import { MessageProvider } from './context/MessageContext';
import { DialogProvider } from './context/DialogContext';
import { PortalPresenceProvider } from './context/PortalPresenceContext';
import CallProvider from './components/calls/CallProvider';
import CallLayer from './components/calls/CallLayer';
import { PortalPostingProvider } from './context/PortalPostingContext';
import { BackgroundProvider, useBackground } from './context/BackgroundContext';
import { isProfileComplete } from './utils/profileUtils';
import { safeReturnTo } from './utils/returnTo';
import { recordPath } from './utils/backNav';
import LoadingSpinner from './components/LoadingSpinner';
import InstallAppPrompt from './components/InstallAppPrompt';
import EnablePushPrompt from './components/EnablePushPrompt';
import CapacitorBootstrap from './components/CapacitorBootstrap';
import LiveEventBanner from './components/LiveEventBanner';
import { initAnalytics, trackPageView } from './services/analytics';
import AppNoticeHost from './components/appNotice/AppNoticeHost';
import { AppNoticeProvider } from './context/AppNoticeContext';
import LasersBackground, { PAGE_INK_VARS } from './components/chrome/LasersBackground';

// Pages
import LandingPage from './pages/LandingPage';
import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/RegisterPage';
import EnterScreen from './components/auth/EnterScreen';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import GoogleCallbackPage from './pages/GoogleCallbackPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import OnboardingPage from './pages/OnboardingPage';
import PartyPreferencePage from './pages/PartyPreferencePage';
import PublicProfilePage from './pages/PublicProfilePage';
import PortalEditorPage from './pages/PortalEditorPage';
import MessagesPage from './pages/MessagesPage';
import PortalLandingPage from './pages/portal/PortalLandingPage';
import PortalAboutPage from './pages/portal/PortalAboutPage';
import PortalApplyPage from './pages/portal/PortalApplyPage';
import PortalThanksPage from './pages/portal/PortalThanksPage';
import PortalHomePage from './pages/portal/PortalHomePage';
import PortalGiveTakePage from './pages/portal/PortalGiveTakePage';
import PortalSendSignalPage from './pages/portal/PortalSendSignalPage';
import PortalSignalDetailPage from './pages/portal/PortalSignalDetailPage';
import PortalMembersDirectoryPage from './pages/portal/PortalMembersDirectoryPage';
import PortalAdminHomePage from './pages/portal/PortalAdminHomePage';
import PortalAdminApplicationsPage from './pages/portal/PortalAdminApplicationsPage';
import PortalAdminApplicationDetailPage from './pages/portal/PortalAdminApplicationDetailPage';
import PortalAdminMembersPage from './pages/portal/PortalAdminMembersPage';
import PortalAdminMemberDetailPage from './pages/portal/PortalAdminMemberDetailPage';
import PortalAdminAdminsPage from './pages/portal/PortalAdminAdminsPage';
import GuestDashboardPage from './pages/GuestDashboardPage';
import HostDashboardPage from './pages/HostDashboardPage';
import ClaimPartiesPage from './pages/ClaimPartiesPage';
import PlannerPage from './pages/PlannerPage';
import EventCreatePage from './pages/EventCreatePage';
import EventSharePage from './pages/EventSharePage';
import MimePage from './pages/MimePage';
import EventOperationPage from './pages/EventOperationPage';
import DoorScanPage from './pages/DoorScanPage';
import EventApplyPage from './pages/EventApplyPage';
import PaymentRecoveryPage from './pages/PaymentRecoveryPage';
import WalletPage from './pages/WalletPage';
import ProLandingPage from './pages/ProLandingPage';
import SubscriptionPage from './pages/SubscriptionPage';
import NotificationsPage from './pages/NotificationsPage';
import NotificationSettingsPage from './pages/NotificationSettingsPage';
import FriendRequestsPage from './pages/FriendRequestsPage';
import SettingsPage from './pages/SettingsPage';
import { AdminShell } from './components/admin/shell';
import AdminPreview from './components/admin/shell/AdminPreview';
import * as Admin from './pages/admin';
import FeaturedWallPage from './pages/FeaturedWallPage';
import EventsAllPage from './pages/EventsAllPage';
import EventsByCityPage from './pages/EventsByCityPage';
import ClubForumPage from './pages/ClubForumPage';
import ClubForumThreadPage from './pages/ClubForumThreadPage';
import SearchResultsPage from './pages/SearchResultsPage';
import PeoplePage from './pages/PeoplePage';
import StartPage from './pages/StartPage';
import SquarePage from './pages/SquarePage';
import CalendarPage from './pages/CalendarPage';
import ProPage from './pages/ProPage';
import ProClaimPage from './pages/ProClaimPage';
import ProDashboardPage from './pages/ProDashboardPage';
import OfferPage from './pages/OfferPage';
import ProSetupPage from './pages/ProSetupPage';
import ProDeskPage from './pages/ProDeskPage';
import ProOfferPage from './pages/ProOfferPage';
import PortalComposerPage from './pages/PortalComposerPage';
import PortalMotionCheck from './pages/PortalMotionCheck';
import PortalShortLinkPage from './pages/PortalShortLinkPage';
import InviteLandingPage from './pages/InviteLandingPage';
import ContactLinkPage from './pages/ContactLinkPage';
import DomainConnectedPage from './pages/DomainConnectedPage';
import PortalPostingToast from './components/stories/PortalPostingToast';
import SignalDetailPage from './pages/SignalDetailPage';
import ClubsListPage from './pages/ClubsListPage';
import ClubCreatePage from './pages/ClubCreatePage';
import ClubPage from './pages/ClubPage';
import PublicClubPage from './pages/PublicClubPage';
import ClubOwnerDashboardPage from './pages/ClubOwnerDashboardPage';
import ClubManagePage from './pages/ClubManagePage';
import GatheringPage from './pages/GatheringPage';
import GatheringWizardPage from './pages/GatheringWizardPage';
import GatheringsManagePage from './pages/GatheringsManagePage';
import GatheringManagePage from './pages/GatheringManagePage';
import TicketPage from './pages/TicketPage';
import TicketClaimPage from './pages/TicketClaimPage';
import GatheringTicketPage from './pages/GatheringTicketPage';
import TicketSuccessPage from './pages/TicketSuccessPage';
import MyTicketsPage from './pages/MyTicketsPage';
import TermsAndConditionsPage from './pages/TermsAndConditionsPage';
import PrivacyPolicyPage from './pages/PrivacyPolicyPage';

import './index.css';

// Initialize Stripe
const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY || '');

// Protected Route wrapper
function ProtectedRoute({ children }) {
  const { isAuthenticated, isLoading } = useAuth();
  const location = useLocation();

  if (isLoading && !isAuthenticated) {
    return <LoadingSpinner fullScreen />;
  }

  if (!isAuthenticated) {
    // Preserve the destination as a query param (survives the login↔register hop and
    // account creation) AND as state, so a QR/deep link lands back here post-auth.
    const dest = encodeURIComponent(location.pathname + location.search);
    return <Navigate to={`/login?redirect=${dest}`} replace state={{ from: location }} />;
  }

  return children;
}

// Gate for /pro/offer/:offerId. A signed-in party sees it directly. A guest (accountless
// buyer) who opens the offer from an email carries an ?access={token} magic link — we
// exchange it for a session on their stub account first, so the offer opens and its pay
// button works on any device, with no password. No token and no session → login, as usual.
function ProOfferGate({ children }) {
  const { isAuthenticated, isLoading, applyGuestSession } = useAuth();
  const location = useLocation();
  const { offerId } = useParams();
  const accessToken = new URLSearchParams(location.search).get('access');
  const [exchanging, setExchanging] = useState(!!accessToken && !isAuthenticated);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (isLoading) return;
      if (isAuthenticated || !accessToken) { setExchanging(false); return; }
      setExchanging(true);
      const r = await proService.guestOfferSession(offerId, accessToken);
      if (cancelled) return;
      if (r.success && r.data?.accessToken) applyGuestSession(r.data);
      else setFailed(true);
      setExchanging(false);
    })();
    return () => { cancelled = true; };
  }, [isLoading, isAuthenticated, accessToken, offerId, applyGuestSession]);

  if ((isLoading && !isAuthenticated) || exchanging) return <LoadingSpinner fullScreen />;
  if (isAuthenticated) return children;
  // A valid-looking token that hasn't failed yet: hold for the effect rather than flashing login.
  if (accessToken && !failed) return <LoadingSpinner fullScreen />;

  const dest = encodeURIComponent(location.pathname + location.search);
  return <Navigate to={`/login?redirect=${dest}`} replace state={{ from: location }} />;
}

// Redirect to dashboard if already authenticated
function PublicRoute({ children }) {
  const { isAuthenticated, isLoading } = useAuth();
  const { profile, isLoading: isUserLoading } = useUser();

  if ((isLoading && !isAuthenticated) || (isUserLoading && !profile)) {
    return <LoadingSpinner fullScreen />;
  }

  if (isAuthenticated) {
    // Nothing saved yet, or a name but nothing else: `/onboard` resumes wherever they stopped.
    if (!profile || !isProfileComplete(profile)) {
      return <Navigate to="/onboard" replace />;
    }
    // NOTE (Phase 7 — frictionless onboarding): onboarding is not FORCED. New signups get a
    // default display name and go straight to the app; `/onboard` stays reachable and is
    // re-surfaced by the resume notice and the profile photo gate. Do not re-add a forced
    // redirect here.
    // Everyone (host or guest) lands on Home, their town's page. Hosts can still jump to /host
    // from the host button in the nav.
    return <Navigate to="/square" replace />;
  }

  return children;
}

/**
 * The four retired onboarding paths → `/onboard`, keeping `?next=` (and dropping `?mode=edit`,
 * which has no meaning now that editing lives in the Portal Editor).
 */
function ToOnboarding() {
  const [params] = useSearchParams();
  const next = safeReturnTo(params.get('next'));
  return <Navigate to={next ? `/onboard?next=${encodeURIComponent(next)}` : '/onboard'} replace />;
}

/** Old builder links /admin/newsletters/:id → /admin/newsletter/:id. */
function NewsletterRedirect() {
  const { id } = useParams();
  return <Navigate to={`/admin/newsletter/${id}`} replace />;
}

/** Every admin section as nested routes, shared by /admin and the dev-only preview. */
function adminSectionRoutes() {
  return (
    <>
        <Route index element={<Admin.TodayPage />} />
        <Route path="today" element={<Navigate to="/admin" replace />} />
        <Route path="stats" element={<Admin.StatsPage />} />
        <Route path="analytics" element={<Admin.AnalyticsPage />} />
        <Route path="user-reports" element={<Admin.UserReportsPage />} />
        <Route path="users" element={<Admin.UsersPage />} />
        <Route path="users/:id" element={<Admin.UsersPage />} />
        <Route path="hosts" element={<Admin.HostsPage />} />
        <Route path="staff" element={<Admin.StaffPage view="people" />} />
        <Route path="staff/roles" element={<Admin.StaffPage view="roles" />} />
        <Route path="staff/roles/:roleKey" element={<Admin.StaffPage view="roles" />} />
        <Route path="staff/:userId" element={<Admin.StaffPage view="people" />} />
        <Route path="wallets" element={<Admin.WalletsPage />} />
        <Route path="transactions" element={<Admin.TransactionsPage />} />
        <Route path="withdrawals" element={<Admin.WithdrawalsPage />} />
        <Route path="disputes" element={<Admin.DisputesPage />} />
        <Route path="fees" element={<Admin.FeesPage />} />
        <Route path="events" element={<Admin.EventsPage />} />
        <Route path="events/:id" element={<Admin.EventsPage />} />
        <Route path="gatherings" element={<Admin.GatheringsAdminPage />} />
        <Route path="gatherings/:id" element={<Admin.GatheringsAdminPage />} />
        <Route path="pick" element={<Admin.PickEventsPage />} />
        <Route path="featured" element={<Admin.FeaturedPage />} />
        <Route path="newsletter" element={<Admin.NewsletterPage />} />
        <Route path="newsletter/:id" element={<Admin.NewsletterPage />} />
        <Route path="newsletters" element={<Navigate to="/admin/newsletter" replace />} />
        <Route path="newsletters/:id" element={<NewsletterRedirect />} />
        <Route path="notices" element={<Admin.NoticesPage view="sheets" />} />
        <Route path="notices/push" element={<Admin.NoticesPage view="push" />} />
        <Route path="brands" element={<Admin.BrandsPage />} />
        <Route path="patrons" element={<Admin.PatronsPage />} />
        <Route path="reports" element={<Admin.ReportsPage />} />
        <Route path="party-engine" element={<Admin.EnginePage />} />
        <Route path="party-engine/runs/:runId" element={<Admin.EnginePage />} />
        <Route path="listed-pros" element={<Admin.ListedProsPage />} />
        <Route path="listed-pros/runs/:runId" element={<Admin.ListedProsPage />} />
        <Route path="ingestion" element={<Admin.IngestionPage />} />
        <Route path="ingestion/:view" element={<Admin.IngestionPage />} />
        <Route path="pro-pages" element={<Admin.ProPagesPage />} />
        <Route path="planner" element={<Admin.PlannerPage />} />
        <Route path="plan-detection" element={<Admin.PlanDetectionPage />} />
        <Route path="settings" element={<Admin.SettingsPage />} />
        <Route path="regions" element={<Admin.RegionsPage />} />
        <Route path="squares" element={<Admin.SquaresPage />} />
        <Route path="audit" element={<Admin.AuditPage />} />
        <Route path="*" element={<Admin.TodayPage />} />
    </>
  );
}

/**
 * The planner is gated by `planner_audience` (off · admins · paid · everyone). Hiding the
 * button is a courtesy; this is what stops a typed URL, and the API refuses the same people
 * on every route regardless. Anyone without it is offered the thing they CAN do.
 */
function RequirePlanner({ children }) {
  const { profile, canUsePlanner, isLoading } = useUser();
  if (isLoading && !profile) return <LoadingSpinner fullScreen />;
  if (!canUsePlanner) return <Navigate to="/clubs/create" replace />;
  return children;
}

function RequireProfile({ children }) {
  const { profile, isLoading } = useUser();

  if (isLoading && !profile) {
    return <LoadingSpinner fullScreen />;
  }

  if (!profile || !isProfileComplete(profile)) {
    return <Navigate to="/onboard" replace />;
  }

  // Phase 7: onboarding is not forced past a display name (see the PublicRoute note).

  return children;
}

// The City Portal is member-only. A LOGGED-OUT visitor to a city's square gets the
// "New in town? You're invited" landing (with that city's name + a local signal)
// instead of a login wall.
function TownSquareGate() {
  const { isAuthenticated, isLoading } = useAuth();
  const { citySlug } = useParams();
  if (isLoading && !isAuthenticated) return <LoadingSpinner fullScreen />;
  if (!isAuthenticated) return <NewHereCity citySlug={citySlug} />;
  return (
    <RequireProfile>
      <SquarePage />
    </RequireProfile>
  );
}

// A club link opens for anyone. A LOGGED-OUT visitor gets the read-only public
// page (name, blurb, counts, "log in to join") — no members, no forum, no
// announcements. Signed-in users keep the full ClubPage untouched.
//
// The route also accepts a club's slug now. ClubPage is built around the GUID,
// so rather than teach it a second addressing scheme we resolve slug → id here
// and redirect; the authed experience below is byte-for-byte what it was.
function ClubRouteGate() {
  const { isAuthenticated, isLoading } = useAuth();
  const { id } = useParams();
  const navigate = useNavigate();
  const isGuid = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(id || '');
  const [resolving, setResolving] = useState(false);

  useEffect(() => {
    if (isLoading || !isAuthenticated || isGuid) return;
    let cancelled = false;
    setResolving(true);
    (async () => {
      const res = await clubService.getPublicClub(id);
      if (cancelled) return;
      if (res.success && res.data?.id) navigate(`/clubs/${res.data.id}`, { replace: true });
      else setResolving(false);
    })();
    return () => { cancelled = true; };
  }, [id, isGuid, isAuthenticated, isLoading, navigate]);

  if (isLoading && !isAuthenticated) return <LoadingSpinner fullScreen />;
  if (!isAuthenticated) return <PublicClubPage />;
  if (!isGuid) return resolving ? <LoadingSpinner fullScreen /> : <PublicClubPage />;
  return (
    <RequireProfile>
      <ClubPage />
    </RequireProfile>
  );
}

/** /events/:id/manage → /host/events/:id (handoff alias, see the route comment). */
/** /pros moved into People → PROS (round-3 handoff §1f). The old directory's filters carry over. */
function RedirectProsToPeople() {
  const { search } = useLocation();
  const from = new URLSearchParams(search);
  const next = new URLSearchParams({ tab: 'pros' });
  ['q', 'r', 'lat', 'lng', 'city', 'anywhere'].forEach((k) => { if (from.get(k)) next.set(k, from.get(k)); });
  if (from.get('craft')) next.set('sub', from.get('craft'));
  if (from.get('places') === '1' || from.get('shops') === '1') next.set('places', '1');
  return <Navigate to={`/people?${next.toString()}`} replace />;
}

function RedirectToHostEvent() {
  const { id } = useParams();
  return <Navigate to={`/host/events/${id}`} replace />;
}

/** /clubs/:id/owner → /clubs/:id/revenue (memberships & revenue moved in the redesign). */
function RedirectToClubRevenue() {
  const { id } = useParams();
  return <Navigate to={`/clubs/${id}/revenue`} replace />;
}

function AppRoutes() {

  return (
    <Routes>
      <Route element={<AppChrome />}>
      {process.env.NODE_ENV === 'development' && <Route path="/portals/motion-check" element={<PortalMotionCheck />} />}
      {/* Dev only: the composer without the sign-in gate, for driving it in a browser (sending still needs a session). */}
      {process.env.NODE_ENV === 'development' && <Route path="/portals/composer-check" element={<PortalComposerPage />} />}
      {/* Public routes (outside theme) */}
      <Route
        path="/"
        element={
          <PublicRoute>
            <LandingPage />
          </PublicRoute>
        }
      />
      <Route
        path="/login"
        element={
          <PublicRoute>
            <LoginPage />
          </PublicRoute>
        }
      />
      {/* Sign-up v3 (Oct 2026): /register is ENTER (Google · Apple · USE EMAIL INSTEAD · LOG IN);
          the email form moved to /register/email. A new account then walks /onboard. */}
      <Route
        path="/register"
        element={
          <PublicRoute>
            <EnterScreen />
          </PublicRoute>
        }
      />
      <Route
        path="/register/email"
        element={
          <PublicRoute>
            <RegisterPage />
          </PublicRoute>
        }
      />
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      {/* Google OAuth redirect target — not wrapped in PublicRoute so the code
          exchange can run before tokens exist. */}
      <Route path="/auth/google/callback" element={<GoogleCallbackPage />} />

      {/* Onboarding — one page, four steps (design_handoff_onboarding_v2). Protected but no
          profile required: finishing this IS what makes a profile complete. */}
      <Route
        path="/onboard"
        element={
          <ProtectedRoute>
            <OnboardingPage />
          </ProtectedRoute>
        }
      />
      {/* The nine-screen flow this replaced. The paths stay as redirects: they are in old
          emails, in the app-notice `primaryHref` of rows written before the rebuild, and in
          people's history. `?next=` rides along so a resume still returns where it started. */}
      <Route path="/hi-i-like-to-party" element={<ToOnboarding />} />
      <Route path="/credential-builder" element={<ToOnboarding />} />
      <Route path="/initialize-credential" element={<ToOnboarding />} />
      <Route path="/onboard/intake" element={<ToOnboarding />} />
      <Route
        path="/party-preference"
        element={
          <ProtectedRoute>
            <PartyPreferencePage />
          </ProtectedRoute>
        }
      />

      {/* Guest dashboard — reachable from the avatar menu (above Hosting).
          The Events tab is the default home, but the original guest dashboard
          stays available here. */}
      <Route
        path="/guest"
        element={
          <ProtectedRoute>
            <RequireProfile>
              <GuestDashboardPage />
            </RequireProfile>
          </ProtectedRoute>
        }
      />

      {/* Host routes (inside theme) */}
      <Route
        path="/host"
        element={
          <ProtectedRoute>
            <RequireProfile>
              <HostDashboardPage />
            </RequireProfile>
          </ProtectedRoute>
        }
      />
      {/* Party Engine: parties seeded by the platform that need a host. `host` is already reserved. */}
      <Route
        path="/host/claim"
        element={
          <ProtectedRoute>
            <RequireProfile>
              <ClaimPartiesPage />
            </RequireProfile>
          </ProtectedRoute>
        }
      />
      {/* One-prompt planner. Full-screen takeover; `host` is already reserved. */}
      <Route path="/host/events/plan" element={<ProtectedRoute><RequireProfile><RequirePlanner><PlannerPage /></RequirePlanner></RequireProfile></ProtectedRoute>} />
      <Route path="/host/events/plan/:id" element={<ProtectedRoute><RequireProfile><RequirePlanner><PlannerPage /></RequirePlanner></RequireProfile></ProtectedRoute>} />
      {/* The creator's view is a path, not a query: the bare path is SIMPLE, `/advanced` is the
          five-step wizard. `?view=` still lands and the page rewrites it to the path. */}
      {['/host/events/create', '/host/events/create/:view'].map((createPath) => (
        <Route
          key={createPath}
          path={createPath}
          element={
            <ProtectedRoute>
              <RequireProfile>
                <EventCreatePage />
              </RequireProfile>
            </ProtectedRoute>
          }
        />
      ))}
      <Route
        path="/host/events/:id"
        element={
          <ProtectedRoute>
            <RequireProfile>
              <EventOperationPage />
            </RequireProfile>
          </ProtectedRoute>
        }
      />
      {/* Handoff alias (docs/plans/2026-09-events-clubs-redesign): the host operations
          surface is spec'd at /events/:id/manage. Canonical stays /host/events/:id — every
          existing link, email and notification points there — so the alias redirects. Two
          segments, so it never collides with /events/:inviteCode. */}
      <Route path="/events/:id/manage" element={<RedirectToHostEvent />} />
      <Route
        path="/host/events/:id/edit"
        element={
          <ProtectedRoute>
            <RequireProfile>
              <EventCreatePage />
            </RequireProfile>
          </ProtectedRoute>
        }
      />
      <Route
        path="/host/events/:id/door"
        element={
          <ProtectedRoute>
            <RequireProfile>
              <DoorScanPage />
            </RequireProfile>
          </ProtectedRoute>
        }
      />
      <Route
        path="/host/events/:id/mime"
        element={
          <ProtectedRoute>
            <RequireProfile>
              <MimePage />
            </RequireProfile>
          </ProtectedRoute>
        }
      />
      <Route
        path="/host/events/:id/share"
        element={
          <ProtectedRoute>
            <RequireProfile>
              <EventSharePage />
            </RequireProfile>
          </ProtectedRoute>
        }
      />
      <Route
        path="/host/stripe-return"
        element={
          <ProtectedRoute>
            <RequireProfile>
              <HostDashboardPage />
            </RequireProfile>
          </ProtectedRoute>
        }
      />
      <Route
        path="/host/stripe-refresh"
        element={
          <ProtectedRoute>
            <RequireProfile>
              <HostDashboardPage />
            </RequireProfile>
          </ProtectedRoute>
        }
      />

      {/* Event application routes */}
      {/* /events/all is the shared "see all" list page — it MUST be declared
          before /events/:inviteCode or the catch-all treats "all" as an invite
          code (docs/plans/2026-08-handoff-layout/03-see-all.md). */}
      <Route path="/events/all" element={<EventsAllPage />} />
      {/* Search results are their own route, separate from /events/all — they
          only share the list layout (DECISIONS D-2). */}
      <Route path="/search" element={<SearchResultsPage />} />
      {/* Crawlable city landing pages. Public, no guard — a page built for search
          has to answer a logged-out request. Deliberately THREE segments: a
          two-segment /events/:citySlug would be ambiguous with the invite-code
          route below, since both would match /events/kelowna. */}
      <Route path="/events/in/:citySlug" element={<EventsByCityPage />} />
      <Route path="/events/in/:citySlug/:category" element={<EventsByCityPage />} />
      <Route path="/events/:inviteCode" element={<EventApplyPage />} />
      <Route path="/e/:inviteCode" element={<EventApplyPage />} />
      {/* Canonical, shareable event URL: /e/kelowna/intro-to-yoga-series. Two segments, so it
          never collides with the one-segment /e/:inviteCode above, and the /e/ prefix keeps every
          city name out of the bare-root /{handle} namespace ("e" is already reserved in BOTH
          lists). The invite-code routes stay live and unredirected — links already shared keep
          working exactly as they did; rel=canonical on the page does the SEO work. */}
      <Route path="/e/:citySlug/:slug" element={<EventApplyPage />} />

      {/* Payment recovery */}
      <Route
        path="/recover-payment/:applicationId"
        element={
          <ProtectedRoute>
            <PaymentRecoveryPage />
          </ProtectedRoute>
        }
      />

      {/* User profile */}
      <Route
        path="/users/:userId"
        element={
          <ProtectedRoute>
            <PublicProfilePage />
          </ProtectedRoute>
        }
      />

      {/* Wallet */}
      <Route
        path="/wallet"
        element={
          <ProtectedRoute>
            <RequireProfile>
              <WalletPage />
            </RequireProfile>
          </ProtectedRoute>
        }
      />

      {/* Golden-Ticket membership (separate feature from the Pro Portal) */}
      <Route
        path="/membership"
        element={
          <ProtectedRoute>
            <RequireProfile>
              <SubscriptionPage />
            </RequireProfile>
          </ProtectedRoute>
        }
      />
      {/* Pro Portal landing — public marketing page (most often reached by a cold shared
          link). Its CTA sends a signed-out visitor through /register?redirect=/pro/setup. */}
      <Route path="/pro" element={<ProLandingPage />} />
      {/* Pro Portal (6a) — setup must precede the :slug page */}
      <Route
        path="/pro/setup"
        element={
          <ProtectedRoute>
            <RequireProfile>
              <ProSetupPage />
            </RequireProfile>
          </ProtectedRoute>
        }
      />
      {/* Pro booking (6b) — Desk + offer negotiation, before the :slug page */}
      <Route
        path="/pro/desk"
        element={
          <ProtectedRoute>
            <RequireProfile>
              <ProDeskPage />
            </RequireProfile>
          </ProtectedRoute>
        }
      />
      <Route
        path="/pro/offer/:offerId"
        element={
          <ProOfferGate>
            <RequireProfile>
              <ProOfferPage />
            </RequireProfile>
          </ProOfferGate>
        }
      />
      <Route path="/pro/flags" element={<Navigate to="/admin/disputes" replace />} />
      {/* Vendor pages: the Pro dashboard (offers, scanner, analytics). */}
      <Route
        path="/pro/dashboard"
        element={
          <ProtectedRoute>
            <RequireProfile>
              <ProDashboardPage />
            </RequireProfile>
          </ProtectedRoute>
        }
      />
      {/* A claimed vendor offer's QR points here. Public: a server's phone opens it. `offer` is reserved. */}
      <Route path="/offer/:claimId" element={<OfferPage />} />
      {/* Listed Pro claim link — public so a logged-out owner sees LOG IN TO CLAIM. */}
      <Route path="/pro/claim/:token" element={<ProClaimPage />} />
      <Route path="/pro/:slug" element={<ProPage />} />
      {/* Every tab has its own link: /pro/{slug}/events opens EVENTS at once. */}
      <Route path="/pro/:slug/:tab" element={<ProPage />} />
      {/* The Pro directory lives on People → PROS now; `pros` stays reserved in both handle lists. */}
      <Route path="/pros" element={<RedirectProsToPeople />} />

      {/* Notifications */}
      <Route
        path="/notifications"
        element={
          <ProtectedRoute>
            <RequireProfile>
              <NotificationsPage />
            </RequireProfile>
          </ProtectedRoute>
        }
      />
      <Route
        path="/notifications/settings"
        element={
          <ProtectedRoute>
            <RequireProfile>
              <NotificationSettingsPage />
            </RequireProfile>
          </ProtectedRoute>
        }
      />

      {/* Friends */}
      <Route
        path="/friends"
        element={
          <ProtectedRoute>
            <RequireProfile>
              <FriendRequestsPage />
            </RequireProfile>
          </ProtectedRoute>
        }
      />

      {/* Settings */}
      <Route
        path="/settings"
        element={
          <ProtectedRoute>
            <RequireProfile>
              <SettingsPage />
            </RequireProfile>
          </ProtectedRoute>
        }
      />

      {/* Clubs */}
      <Route
        path="/clubs"
        element={
          <ProtectedRoute>
            <RequireProfile>
              <ClubsListPage />
            </RequireProfile>
          </ProtectedRoute>
        }
      />
      <Route
        path="/clubs/create"
        element={
          <ProtectedRoute>
            <RequireProfile>
              <ClubCreatePage />
            </RequireProfile>
          </ProtectedRoute>
        }
      />
      {/* Public: logged-out visitors get the read-only club page; members get
          the full ClubPage. Accepts a slug or an id. */}
      <Route path="/clubs/:id" element={<ClubRouteGate />} />
      <Route
        path="/clubs/:id/forum"
        element={(
          <ProtectedRoute>
            <ClubForumPage />
          </ProtectedRoute>
        )}
      />
      <Route
        path="/clubs/:id/forum/:threadId"
        element={(
          <ProtectedRoute>
            <ClubForumThreadPage />
          </ProtectedRoute>
        )}
      />
      <Route
        path="/clubs/:id/manage"
        element={
          <ProtectedRoute>
            <RequireProfile>
              <ClubManagePage />
            </RequireProfile>
          </ProtectedRoute>
        }
      />
      <Route
        path="/clubs/:id/revenue"
        element={
          <ProtectedRoute>
            <RequireProfile>
              <ClubOwnerDashboardPage />
            </RequireProfile>
          </ProtectedRoute>
        }
      />
      {/* Old owner-dashboard path, kept for bookmarks + notifications. */}
      <Route path="/clubs/:id/owner" element={<RedirectToClubRevenue />} />
      <Route
        path="/clubs/:id/gatherings"
        element={
          <ProtectedRoute>
            <RequireProfile>
              <GatheringsManagePage />
            </RequireProfile>
          </ProtectedRoute>
        }
      />
      <Route
        path="/clubs/:id/gatherings/new"
        element={
          <ProtectedRoute>
            <RequireProfile>
              <GatheringWizardPage />
            </RequireProfile>
          </ProtectedRoute>
        }
      />
      <Route
        path="/clubs/:id/gatherings/:gatheringId/edit"
        element={
          <ProtectedRoute>
            <RequireProfile>
              <GatheringWizardPage />
            </RequireProfile>
          </ProtectedRoute>
        }
      />
      <Route
        path="/clubs/:id/gatherings/:gatheringId/manage"
        element={
          <ProtectedRoute>
            <RequireProfile>
              <GatheringManagePage />
            </RequireProfile>
          </ProtectedRoute>
        }
      />
      <Route
        path="/clubs/:id/gatherings/:gatheringId"
        element={
          <ProtectedRoute>
            <RequireProfile>
              <GatheringPage />
            </RequireProfile>
          </ProtectedRoute>
        }
      />
      <Route
        path="/gatherings/:gatheringId/manage"
        element={
          <ProtectedRoute>
            <RequireProfile>
              <GatheringManagePage />
            </RequireProfile>
          </ProtectedRoute>
        }
      />
      <Route
        path="/gatherings/:gatheringId/edit"
        element={
          <ProtectedRoute>
            <RequireProfile>
              <GatheringWizardPage />
            </RequireProfile>
          </ProtectedRoute>
        }
      />
      <Route
        path="/gatherings/new"
        element={
          <ProtectedRoute>
            <RequireProfile>
              <GatheringWizardPage />
            </RequireProfile>
          </ProtectedRoute>
        }
      />
      <Route
        path="/gatherings/:gatheringId"
        element={
          <ProtectedRoute>
            <RequireProfile>
              <GatheringPage />
            </RequireProfile>
          </ProtectedRoute>
        }
      />
      <Route
        path="/clubs/invite/:code"
        element={
          <ProtectedRoute>
            <RequireProfile>
              <ClubPage />
            </RequireProfile>
          </ProtectedRoute>
        }
      />

      {/* Admin — one shell for every staff section (docs/plans/2026-09-admin-rbac).
          Every section is /admin/<section>; the shell renders the rail from the
          person's key and the page as an Outlet. /curator, /admin/newsletters and
          /pro/flags redirect to their new homes. */}
      <Route
        path="/admin"
        element={
          <ProtectedRoute>
            <RequireProfile>
              <AdminShell />
            </RequireProfile>
          </ProtectedRoute>
        }
      >
        {adminSectionRoutes()}
      </Route>
      {/* Development only: the shell through a fixture key, for layout checks without a login. */}
      {process.env.NODE_ENV === 'development' && (
        <Route path="/admin-preview" element={<AdminPreview />}>
          {adminSectionRoutes()}
        </Route>
      )}
      {/* Curator — folded into the admin shell (EVENTS › PICK EVENTS). */}
      <Route path="/curator" element={<Navigate to="/admin/pick" replace />} />
      {/* Featured Wall — public, anonymous-allowed. The Events tab / v6 home. */}
      <Route path="/featured" element={<FeaturedWallPage />} />
      {/* Party Scroll v6 primary tabs */}
      <Route
        path="/people"
        element={
          <ProtectedRoute>
            <RequireProfile>
              <PeoplePage />
            </RequireProfile>
          </ProtectedRoute>
        }
      />
      <Route
        path="/start"
        element={
          <ProtectedRoute>
            <RequireProfile>
              <StartPage />
            </RequireProfile>
          </ProtectedRoute>
        }
      />
      {/* Public: logged-out visitors get the "New here" city landing; members get the square. */}
      <Route path="/square" element={<TownSquareGate />} />
      <Route path="/square/:citySlug" element={<TownSquareGate />} />
      <Route
        path="/calendar"
        element={
          <ProtectedRoute>
            <RequireProfile>
              <CalendarPage />
            </RequireProfile>
          </ProtectedRoute>
        }
      />
      {/* Portals (the broadcast formerly called a Signal). The word changed in Sept 2026;
          the API, the columns and the notification refs deliberately did not — see
          docs/plans/2026-09-portals-stories/PLAN.md. /signals/* stays forever as a redirect:
          those links are already in inboxes and push notifications. */}
      <Route
        path="/portals/new"
        element={
          <ProtectedRoute>
            <RequireProfile>
              <PortalComposerPage />
            </RequireProfile>
          </ProtectedRoute>
        }
      />
      {/* Edit = the Portal's own page with the viewer-style editor open over it. */}
      <Route
        path="/portals/:id/edit"
        element={
          <ProtectedRoute>
            <RequireProfile>
              <SignalDetailPage editor />
            </RequireProfile>
          </ProtectedRoute>
        }
      />
      {/* Public — a shared Portal link opens for anyone; joining prompts sign-up. */}
      <Route path="/portals/:id" element={<SignalDetailPage />} />
      {/* Short Portal links from shared cards. `p` is a reserved handle (both lists). */}
      <Route path="/p/:code" element={<PortalShortLinkPage />} />
      {/* A club's / Pro page's contact link + QR. Public: whoever scans it has no account yet. `join` is reserved (both lists). */}
      <Route path="/join/:code" element={<ContactLinkPage />} />
      <Route path="/domain-connected" element={<DomainConnectedPage />} />
      {/* A printed invite card's QR. Public; signed-in viewers are sent to the inviter's page. `i` is reserved (both lists). */}
      <Route path="/i/:code" element={<InviteLandingPage />} />
      <Route path="/signals/*" element={<SignalsLegacyRedirect />} />
      {/* Ticket routes */}
      <Route path="/ticket/success" element={<TicketSuccessPage />} />
      {/* Before /ticket/:ticketCode — a claim token is not a ticket code, and the
          looser route would swallow it. Public on purpose: whoever was sent this
          usually has no account yet, and the page has to show them what they're
          being offered before it asks them to make one. */}
      <Route path="/ticket/claim/:token" element={<TicketClaimPage />} />
      {/* Native gathering seats (Oct 2026): the seat behind a QR, and the other end of a seat offer. */}
      <Route path="/gatherings/tickets/claim/:token" element={<TicketClaimPage kind="gathering" />} />
      <Route
        path="/gt/:code"
        element={
          <ProtectedRoute>
            <GatheringTicketPage />
          </ProtectedRoute>
        }
      />
      <Route path="/ticket/:ticketCode" element={<TicketPage />} />
      <Route
        path="/my-tickets"
        element={
          <ProtectedRoute>
            <RequireProfile>
              <MyTicketsPage />
            </RequireProfile>
          </ProtectedRoute>
        }
      />
      <Route
        path="/messages"
        element={
          <ProtectedRoute>
            <RequireProfile>
              <MessagesPage />
            </RequireProfile>
          </ProtectedRoute>
        }
      />
      {/* Portal Noir — Reza's club. Landing + About are public; apply/me/host
          require auth; host additionally requires the superadmin role.

          The club lived at /portal until the app itself took that name. Every
          link a member already has — DMs, invite codes, stored notification
          paths, the emails we've already sent — points at the old prefix, so it
          redirects rather than 404s. Keep this until those have aged out. */}
      {/* The Portal Editor — every edit of your own Portal Page after onboarding (the intake
          stepper is first-time only). A static segment, so it wins over the /portal/* redirect
          below; Portal Noir never had an /edit path. 'portal' is already a reserved handle. */}
      <Route
        path="/portal/edit"
        element={
          <ProtectedRoute>
            <RequireProfile>
              <PortalEditorPage />
            </RequireProfile>
          </ProtectedRoute>
        }
      />
      <Route path="/portal/*" element={<PortalLegacyRedirect />} />
      {/* Portal Noir v1 — public (state-aware) */}
      <Route path="/noir" element={<PortalLandingPage />} />
      <Route path="/noir/about" element={<PortalAboutPage />} />
      <Route path="/noir/apply" element={<ProtectedRoute><PortalApplyPage /></ProtectedRoute>} />
      <Route path="/noir/thanks" element={<ProtectedRoute><PortalThanksPage /></ProtectedRoute>} />
      {/* Member-facing */}
      <Route path="/noir/home" element={<ProtectedRoute><PortalHomePage /></ProtectedRoute>} />
      <Route path="/noir/members" element={<ProtectedRoute><PortalMembersDirectoryPage /></ProtectedRoute>} />
      <Route path="/noir/give-take" element={<ProtectedRoute><PortalGiveTakePage /></ProtectedRoute>} />
      <Route path="/noir/signals/new" element={<ProtectedRoute><PortalSendSignalPage /></ProtectedRoute>} />
      <Route path="/noir/signals/:eventId/edit" element={<ProtectedRoute><PortalSendSignalPage /></ProtectedRoute>} />
      <Route path="/noir/signals/:eventId" element={<ProtectedRoute><PortalSignalDetailPage /></ProtectedRoute>} />
      {/* Admin — server-side gate is authoritative; frontend just bounces */}
      <Route path="/noir/admin" element={<ProtectedRoute><PortalAdminHomePage /></ProtectedRoute>} />
      <Route path="/noir/admin/applications" element={<ProtectedRoute><PortalAdminApplicationsPage /></ProtectedRoute>} />
      <Route path="/noir/admin/applications/:id" element={<ProtectedRoute><PortalAdminApplicationDetailPage /></ProtectedRoute>} />
      <Route path="/noir/admin/members" element={<ProtectedRoute><PortalAdminMembersPage /></ProtectedRoute>} />
      <Route path="/noir/admin/members/:userId" element={<ProtectedRoute><PortalAdminMemberDetailPage /></ProtectedRoute>} />
      <Route path="/noir/admin/admins" element={<ProtectedRoute><PortalAdminAdminsPage /></ProtectedRoute>} />
      {/* Legal */}
      <Route path="/terms" element={<TermsAndConditionsPage />} />
      <Route path="/privacy" element={<PrivacyPolicyPage />} />

      {/* Bare-root public profile URLs: partyresume.com/{handle}.
          MUST come last so every named route above wins by specificity.
          Reserved handles can never reach this because the backend's
          ReservedHandles list rejects them at sign-up — and we double-check
          inside PublicProfilePage as defense in depth. */}
      <Route path="/:handle" element={<PublicProfilePage />} />

      {/* Catch all - redirect to landing */}
      <Route path="*" element={<Navigate to="/" replace />} />
      </Route>
    </Routes>
  );
}

/**
 * Renders the app-level background. Defaults to the LASERS shader (design_handoff_lasers_
 * background, Oct 2026 — the pastel cloud skybox is retired), but pages can override it via
 * useBackground().setOverrideUrl(...) — used by ClubPage so a club's custom background fully
 * replaces the global site background. The lasers layer is fixed and sits behind the page
 * (`isolation: isolate` keeps its z-index -1 inside this wrapper); text placed straight on it
 * reads the `--pr-page-ink` / `--pr-page-sub` variables set here (white / #C4C4C4). An
 * override image keeps the old black-on-light defaults, since we can't know how dark it is.
 */
function AppBackground({ children }) {
  const { overrideUrl } = useBackground();
  const usingOverride = !!overrideUrl;

  return (
    <div
      // app-bg is the hook the viewport lock needs: this wrapper is min-h-screen,
      // so on a locked full-viewport route it stays taller than the page and its
      // wallpaper shows in the strip the keyboard is about to cover. See
      // `.app-locked .app-bg` in index.css. The lasers layer is fixed, so it fills
      // that strip whatever the wrapper's height.
      className="app-bg min-h-screen"
      data-page-bg={usingOverride ? 'override' : 'lasers'}
      style={
        usingOverride
          ? {
              backgroundImage: `url(${overrideUrl})`,
              backgroundRepeat: 'no-repeat',
              backgroundPosition: 'center center',
              backgroundAttachment: 'fixed',
              backgroundSize: 'cover',
            }
          : { position: 'relative', isolation: 'isolate', background: '#000', ...PAGE_INK_VARS }
      }
    >
      {!usingOverride && <LasersBackground />}
      {children}
    </div>
  );
}

/**
 * Resets the window scroll to the top on every route (pathname) change.
 * Without this, React Router preserves the previous scroll position, so
 * navigating from a scrolled page lands you mid-screen — especially confusing
 * on mobile. Keyed on pathname only, so in-page search-param changes (filters,
 * ?thread=, ?category=) don't yank the scroll. Anchor (#hash) links are left
 * to the browser.
 */
function ScrollToTop() {
  const { pathname, hash } = useLocation();
  const navType = useNavigationType();
  useEffect(() => {
    // Remember where we came from so "back" affordances (e.g. the event-page
    // breadcrumb) can use real history navigation to restore scroll.
    recordPath(pathname);
    if (hash) return;
    // On POP (back/forward) leave the scroll alone so pages that restore their
    // own position (e.g. the events wall) can put you back where you were.
    if (navType === 'POP') return;
    window.scrollTo(0, 0);
  }, [pathname, hash, navType]);
  return null;
}

/** Mounts once, fires a page_view on every route change; the client also tracks
 * session + engagement time and flushes on visibility/unload. */
function AnalyticsTracker() {
  const { pathname } = useLocation();
  useEffect(() => { initAnalytics(); }, []);
  useEffect(() => { trackPageView(pathname); }, [pathname]);
  return null;
}

/**
 * /signals/* → /portals/* — the broadcast's old address.
 *
 * Sept 2026: Signals became Portals in every word the user reads. The paths people already
 * hold (emailed links, push notifications, anything shared) keep working; the query string and
 * anything after the first segment ride along.
 */
function SignalsLegacyRedirect() {
  const location = useLocation();
  const rest = location.pathname.replace(/^\/signals/, '');
  return <Navigate to={`/portals${rest}${location.search}${location.hash}`} replace />;
}

/**
 * /portal/* → /noir/* — the club's old address.
 *
 * Preserves the rest of the path and the query string, so an invite link like
 * /portal?invite=ABC and a deep link like /portal/signals/{id} both survive.
 * `replace` keeps the dead URL out of the back-stack.
 */
function PortalLegacyRedirect() {
  const location = useLocation();
  const rest = location.pathname.replace(/^\/portal/, '');
  return <Navigate to={`/noir${rest}${location.search}`} replace />;
}

/**
 * Where this document load started, read once at module scope.
 *
 * It decides whether the app opens through its entry ring, and that answer must not change
 * later: re-reading `window.location` on a render would re-arm the ring after a route change and
 * play an entrance over a page the user is already standing on.
 */
const BOOT_PATH = typeof window === 'undefined' ? '/' : window.location.pathname;

function App() {
  return (
    // The OAuth callback is a round-trip's machinery, not a destination — it opens without the
    // ring so a Google sign-in plays ONE portal, the one that reveals where you were headed.
    <Router initialTransition={!isOauthHandoffPath(BOOT_PATH)}>
      <ThemeProvider>
        <AuthProvider>
          <UserProvider>
            <NotificationProvider>
              <MessageProvider>
              <DialogProvider>
                {/* One call for the whole app (Oct 2026): above the router, so navigating never ends it. */}
                <CallProvider>
                {/* Ring state for everyone on screen, batched into one request. */}
                <PortalPresenceProvider>
                {/* Your Portal keeps uploading after you leave the composer. */}
                <PortalPostingProvider>
                <BackgroundProvider>
                  <Elements stripe={stripePromise}>
                    <AppNoticeProvider>
                    <AppBackground>
                      {/* Native shell lifecycle — deep links, back button,
                          push tap routing, status bar, splash. No-op on web. */}
                      <CapacitorBootstrap />
                      <ScrollToTop />
                      <AnalyticsTracker />
                      {/* Every route change goes through a Portal: a ring opens where you
                          tapped and the next page arrives through its hole. The navbar and the
                          bottom bar are outside it on purpose — chrome doesn't move. */}
                      <AppRoutes />
                      {/* The call's full screen, floating window / card, and remote audio. */}
                      <CallLayer />
                      {/* Party Scroll v6 — app-wide mobile bottom nav
                          (Events/People/Clubs/+). Self-hides on unauthenticated,
                          onboarding, Portal, and full-bleed routes. */}
      <PortalPostingToast />
                      {/* Persistent door-scan shortcut for hosts/co-hosts of a live event. */}
                      <LiveEventBanner />
                      {/* Add-to-home-screen prompt — fixed bottom-of-screen,
                          fires 30s after first visit, 14-day cooldown if dismissed,
                          no-op if running as installed PWA or inside Capacitor. */}
                      <InstallAppPrompt />
                      {/* Soft-asks an authed user to enable Web Push after 45s. */}
                      <EnablePushPrompt />
                      {/* In-app notices: the conditional resume prompt and admin
                          announcements. Modal on desktop, bottom sheet on mobile.
                          All timing rules live server-side.
                          docs/plans/2026-08-handoff-layout/09-app-notices.md */}
                      <AppNoticeHost />
                    </AppBackground>
                    </AppNoticeProvider>
                  </Elements>
                </BackgroundProvider>
                </PortalPostingProvider>
                </PortalPresenceProvider>
                </CallProvider>
              </DialogProvider>
              </MessageProvider>
            </NotificationProvider>
          </UserProvider>
        </AuthProvider>
      </ThemeProvider>
    </Router>
  );
}

export default App;
